<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, markRaw, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import DividerComponent from '@/components/ui/DividerComponent.vue';
import ResolveReferralRewardComponent from '@/components/referralReward/ResolveReferralRewardComponent.vue';
import ReferralRewardService from '@/services/referralReward/referralRewardService.ts';
import { useAuthStore } from '@/stores/authStore.ts';
import { displayModal } from '@/utils/modalUtils.ts';
import { displayToast } from '@/utils/toastUtils.ts';
import { ReferralRewardStatusEnum } from '@/enums/referralReward_enum.ts';
import {
  formatEuros,
  formatFullDate,
  formatItemValue,
  formatReferralRewardStatus
} from '@/utils/formatters.ts';

import type { DataTableHeader } from 'vuetify/framework';
import type { ModalOptions } from '@/types/stores/modalStore.type.ts';
import type {
  ReferralReward,
  ReferralUser,
  ResolveReferralRewardRequest
} from '@/types/services/referralReward/referralReward.type.ts';

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const rewardId = Number(route.params.id)
const reward = ref<ReferralReward | null>(null)
const loading = ref<boolean>(false)

const canAct = computed<boolean>(() => authStore.hasPermission(['UPDATE_REFERRAL_REWARD']))
// Retry only on FAILED. On REVIEW it could pay twice, so only "resolve" is offered there.
const canRetry = computed<boolean>(() => canAct.value && reward.value?.status === ReferralRewardStatusEnum.FAILED)
const canResolve = computed<boolean>(() =>
  canAct.value && (
    reward.value?.status === ReferralRewardStatusEnum.FAILED ||
    reward.value?.status === ReferralRewardStatusEnum.REVIEW
  )
)
const needsAttention = computed<boolean>(() =>
  reward.value?.status === ReferralRewardStatusEnum.FAILED ||
  reward.value?.status === ReferralRewardStatusEnum.REVIEW
)

const allocationHeaders = computed<DataTableHeader[]>(() => [
  { title: t('views.referralRewardDetail.allocations.mandate'), key: 'mandateName', sortable: false, align: 'start' },
  { title: t('views.referralRewardDetail.allocations.amount'), key: 'amount', sortable: false, align: 'start' },
  { title: t('views.referralRewardDetail.allocations.status'), key: 'status', sortable: false, align: 'start' },
  { title: t('views.referralRewardDetail.allocations.sharePrice'), key: 'sharePrice', sortable: false, align: 'start' },
  { title: t('views.referralRewardDetail.allocations.investedAt'), key: 'investedAt', sortable: false, align: 'start' },
  { title: t('views.referralRewardDetail.allocations.transaction'), key: 'userTransactionId', sortable: false, align: 'start' },
])

function userLabel(user: ReferralUser): string {
  return `${user.firstName} ${user.lastName}`.trim() || user.email
}

async function fetchReward(): Promise<void> {
  try {
    loading.value = true
    reward.value = await ReferralRewardService.get(rewardId)
  } catch {
    // Error toast already shown (404 referral_reward_not_found_error_key included).
    await router.push('/referral-rewards')
  } finally {
    loading.value = false
  }
}

onMounted(fetchReward)

// A 409 means someone else handled the reward in the meantime: reload to show its real state.
async function runAction(action: () => Promise<ReferralReward>, successMessage: string): Promise<void> {
  try {
    loading.value = true
    reward.value = await action()
    displayToast(successMessage, 'success')
  } catch {
    try {
      reward.value = await ReferralRewardService.get(rewardId)
    } catch {
      // ignore: the original error was already displayed
    }
  } finally {
    loading.value = false
  }
}

async function retryReward(): Promise<void> {
  const options: ModalOptions = {
    title: t('views.referralRewardDetail.retryConfirm.title'),
    message: t('views.referralRewardDetail.retryConfirm.message')
  }
  const modal = await displayModal(options)

  if (modal) {
    await runAction(() => ReferralRewardService.retry(rewardId), t('views.referralRewardDetail.retrySuccess'))
  }
}

async function resolveReward(): Promise<void> {
  const modal = await displayModal({
    component: markRaw(ResolveReferralRewardComponent),
    props: {
      showReviewWarning: reward.value?.status === ReferralRewardStatusEnum.REVIEW
    }
  })

  if (modal && typeof modal === 'object') {
    const { note } = modal as ResolveReferralRewardRequest
    await runAction(() => ReferralRewardService.resolve(rewardId, note), t('views.referralRewardDetail.resolveSuccess'))
  }
}
</script>

<template>
  <MainTitleComponent icon="PhGift">
    {{ t('views.referralRewardDetail.title', { id: rewardId }) }}

    <template v-if="reward && (canRetry || canResolve)" #actions>
      <v-col cols="12" sm="auto" class="d-flex flex-column flex-sm-row ga-3">
        <v-btn
          v-if="canRetry"
          :disabled="loading"
          :loading="loading"
          variant="outlined"
          @click.prevent="retryReward"
        >
          {{ t('views.referralRewardDetail.buttons.retry') }}
          <template #prepend><v-icon icon="PhArrowsClockwise" /></template>
        </v-btn>
        <v-btn
          v-if="canResolve"
          :disabled="loading"
          :loading="loading"
          @click.prevent="resolveReward"
        >
          {{ t('views.referralRewardDetail.buttons.resolve') }}
          <template #prepend><v-icon icon="PhCheck" /></template>
        </v-btn>
      </v-col>
    </template>
  </MainTitleComponent>

  <v-skeleton-loader v-if="!reward" type="article, table" />

  <template v-else>
    <v-alert
      v-if="needsAttention"
      :type="reward.status === ReferralRewardStatusEnum.REVIEW ? 'warning' : 'error'"
      class="mb-4"
      variant="outlined"
    >
      {{ t(`views.referralRewardDetail.attention.${reward.status}`) }}
      <div v-if="reward.lastError" class="mt-2">
        <strong>{{ t('views.referralRewardDetail.lastError') }} :</strong> {{ reward.lastError }}
      </div>
    </v-alert>

    <v-row class="details">
      <v-col cols="12" sm="6" md="4">
        <p class="text-label">{{ t('views.referralRewardDetail.status') }}</p>
        <v-chip :color="formatReferralRewardStatus(reward.status)" size="small">
          {{ t(`views.referralRewardList.status.${reward.status}`) }}
        </v-chip>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <p class="text-label">{{ t('views.referralRewardDetail.amount') }}</p>
        <p class="text-value">{{ formatEuros(reward.amount) }}</p>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <p class="text-label">{{ t('views.referralRewardDetail.attempts') }}</p>
        <p class="text-value">{{ reward.attempts }}</p>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <p class="text-label">{{ t('views.referralRewardDetail.createdAt') }}</p>
        <p class="text-value">{{ formatFullDate(reward.createdAt) }}</p>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <p class="text-label">{{ t('views.referralRewardDetail.paidAt') }}</p>
        <p class="text-value">{{ formatFullDate(reward.paidAt) }}</p>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <p class="text-label">{{ t('views.referralRewardDetail.notifiedAt') }}</p>
        <p class="text-value">
          {{ reward.notifiedAt ? formatFullDate(reward.notifiedAt) : t('views.referralRewardDetail.notNotified') }}
        </p>
      </v-col>
      <v-col v-if="!needsAttention && reward.lastError" cols="12" sm="6" md="4">
        <p class="text-label">{{ t('views.referralRewardDetail.lastError') }}</p>
        <p class="text-value">{{ reward.lastError }}</p>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row class="details">
      <v-col cols="12" md="6">
        <p class="text-label font-weight-bold">{{ t('views.referralRewardDetail.referrer') }}</p>
        <p class="text-value">{{ userLabel(reward.referrer) }}</p>
        <p class="text-label">{{ reward.referrer.email }} (#{{ reward.referrer.id }})</p>
      </v-col>
      <v-col cols="12" md="6">
        <p class="text-label font-weight-bold">{{ t('views.referralRewardDetail.referee') }}</p>
        <p class="text-value">{{ userLabel(reward.referee) }}</p>
        <p class="text-label">{{ reward.referee.email }} (#{{ reward.referee.id }})</p>
      </v-col>
    </v-row>

    <DividerComponent />

    <v-row>
      <v-col cols="12">
        <p class="text-label font-weight-bold mb-2">{{ t('views.referralRewardDetail.allocations.title') }}</p>
        <v-data-table
          :headers="allocationHeaders"
          :items="reward.allocations"
          :items-per-page="-1"
          :no-data-text="t('views.referralRewardDetail.allocations.empty')"
          class="rounded-std"
          hover
        >
          <template #[`item.amount`]="{ item }">
            {{ formatEuros(item.amount) }}
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip
              :color="item.status === 'INVESTED' ? 'success' : (item.status === 'REVIEW' ? 'error' : 'warning')"
              size="small"
            >
              {{ t(`views.referralRewardDetail.allocations.statuses.${item.status}`) }}
            </v-chip>
          </template>
          <template #[`item.sharePrice`]="{ item }">
            {{ item.sharePrice ?? t('common.noData') }}
          </template>
          <template #[`item.investedAt`]="{ item }">
            {{ formatFullDate(item.investedAt) }}
          </template>
          <template #[`item.userTransactionId`]="{ item }">
            {{ item.userTransactionId ?? t('common.noData') }}
          </template>
          <template #bottom />
        </v-data-table>
      </v-col>
    </v-row>

    <template v-if="reward.resolvedAt">
      <DividerComponent />
      <v-row class="details">
        <v-col cols="12" sm="6" md="4">
          <p class="text-label">{{ t('views.referralRewardDetail.resolvedAt') }}</p>
          <p class="text-value">{{ formatFullDate(reward.resolvedAt) }}</p>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <p class="text-label">{{ t('views.referralRewardDetail.resolvedBy') }}</p>
          <p class="text-value">#{{ reward.resolvedById }}</p>
        </v-col>
        <v-col cols="12">
          <p class="text-label">{{ t('views.referralRewardDetail.resolutionNote') }}</p>
          <p class="text-value">{{ formatItemValue(reward.resolutionNote) }}</p>
        </v-col>
      </v-row>
    </template>
  </template>
</template>
